import { supabase } from '@/integrations/supabase/client';
import type { UpdateProfileInput, Profile, AvatarUploadOptions, AvatarUploadResult } from './types';
import { DEFAULT_AVATAR_OPTIONS } from './types';

const handleProfileError = (error: any, operation: string) => {
  console.error(`[Profile Service] ${operation} failed:`, error);
  
  // Map common RPC errors
  if (error.code === 'PGRST401') {
    throw new Error('User not authenticated');
  }
  if (error.code === 'PGRST403') {
    throw new Error('Insufficient permissions');
  }
  if (error.code === 'PGRST404') {
    throw new Error('Profile not found');
  }
  
  throw new Error(error.message || `Failed to ${operation}`);
};

/** Sentinel understood by the profile RPCs to clear avatar_url. */
const CLEAR_VALUE = '';

const avatarParam = (value: string | null | undefined): string | null =>
  value === null ? CLEAR_VALUE : (value || null);

/**
 * Derive the storage object path from either a full public URL or an already
 * relative path (`<companyId>/<authUserId>/avatar.ext`).
 */
export const deriveAvatarStoragePath = (urlOrPath: string): string | null => {
  if (!urlOrPath) return null;
  const marker = '/storage/v1/object/public/avatars/';
  const idx = urlOrPath.indexOf(marker);
  if (idx !== -1) {
    const raw = urlOrPath.slice(idx + marker.length).split('?')[0];
    return decodeURIComponent(raw) || null;
  }
  if (urlOrPath.startsWith('http')) return null;
  return urlOrPath.split('?')[0] || null;
};

export const profileService = {
  /**
   * Get current user's profile using RPC
   */
  getMyProfile: async (): Promise<Profile> => {
    try {
      const { data, error } = await supabase.rpc('get_my_profile');
      
      if (error) throw error;
      return data;
    } catch (error) {
      handleProfileError(error, 'get profile');
      throw error;
    }
  },

  /**
   * Update current user's profile using RPC
   */
  updateMyProfile: async (input: UpdateProfileInput): Promise<Profile> => {
    try {
      const { data, error } = await supabase.rpc('update_my_profile', {
        p_preferred_name: input.preferredName || null,
        p_first_name: input.firstName || null,
        p_last_name: input.lastName || null,
        p_address: input.address || null,
        p_email: input.email || null,
        p_mobile_phone: input.mobilePhone || null,
        p_date_of_birth: input.dateOfBirth || null,
        p_emergency_contact_name: input.emergencyContactName || null,
        p_emergency_contact_phone: input.emergencyContactPhone || null,
        p_avatar_url: avatarParam(input.avatarUrl),
        // weekly_hours / fte are payroll-impacting and ignored by update_my_profile
        p_weekly_hours: null,
        p_fte: null,
      });

      if (error) throw error;
      return data;
    } catch (error) {
      handleProfileError(error, 'update profile');
      throw error;
    }
  },


  /**
   * Update another user's profile (admin/manager only) using RPC
   */
  updateUserProfile: async (authUserId: string, input: UpdateProfileInput): Promise<Profile> => {
    try {
      const rpcParams = {
        p_user_id: authUserId,
        p_preferred_name: input.preferredName || null,
        p_first_name: input.firstName || null,
        p_last_name: input.lastName || null,
        p_address: input.address || null,
        p_email: input.email || null,
        p_mobile_phone: input.mobilePhone || null,
        p_date_of_birth: input.dateOfBirth || null,
        p_emergency_contact_name: input.emergencyContactName || null,
        p_emergency_contact_phone: input.emergencyContactPhone || null,
        p_avatar_url: avatarParam(input.avatarUrl),
        p_weekly_hours: input.weeklyHours !== undefined ? input.weeklyHours : null, // Phase 5
        p_fte: input.fte !== undefined ? input.fte : null, // Phase 5
      };
      
      const { data, error } = await supabase.rpc('update_user_profile', rpcParams);

      if (error) throw error;
      return data;
    } catch (error) {
      handleProfileError(error, 'update user profile');
      throw error;
    }
  },

  /**
   * Validate image file before upload
   */
  validateImageFile: (file: File, options: AvatarUploadOptions = DEFAULT_AVATAR_OPTIONS): void => {
    // Check file size
    if (file.size > (options.maxSizeBytes || DEFAULT_AVATAR_OPTIONS.maxSizeBytes!)) {
      throw new Error(`File size must be less than ${Math.round((options.maxSizeBytes || DEFAULT_AVATAR_OPTIONS.maxSizeBytes!) / 1024 / 1024)}MB`);
    }

    // Check file type
    const allowedTypes = options.allowedTypes || DEFAULT_AVATAR_OPTIONS.allowedTypes!;
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`File type must be one of: ${allowedTypes.join(', ')}`);
    }
  },

  /**
   * Compress image file
   */
  compressImage: (file: File, quality: number = 0.8): Promise<File> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions (max 512x512)
        const maxSize = 512;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          file.type,
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  },

  /**
   * Upload avatar to Supabase Storage
   */
  uploadAvatar: async (file: File, authUserId: string, companyId: string): Promise<AvatarUploadResult> => {
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `avatar.${fileExt}`;
      const filePath = `${companyId}/${authUserId}/${fileName}`;

      // Upload to storage
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true, // Replace existing file
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      return {
        // Cache-busting so replaced avatars refresh immediately
        url: `${urlData.publicUrl}?v=${Date.now()}`,
        path: filePath,
        size: file.size,
      };
    } catch (error) {
      handleProfileError(error, 'upload avatar');
      throw error;
    }
  },

  /**
   * Delete avatar from Supabase Storage
   */
  deleteAvatar: async (urlOrPath: string): Promise<void> => {
    try {
      const filePath = deriveAvatarStoragePath(urlOrPath);
      if (!filePath) return;

      const { error } = await supabase.storage
        .from('avatars')
        .remove([filePath]);

      if (error) throw error;
    } catch (error) {
      console.warn('[Profile Service] Failed to delete old avatar:', error);
      // Don't throw error for cleanup failures
    }
  },

  /**
   * Remove an avatar: delete the stored object AND clear avatar_url on the profile.
   * `targetAuthUserId` is only required when removing another user's avatar.
   */
  removeAvatar: async (urlOrPath: string, targetAuthUserId?: string): Promise<void> => {
    await profileService.deleteAvatar(urlOrPath);

    if (targetAuthUserId) {
      await profileService.updateUserProfile(targetAuthUserId, { avatarUrl: null });
    } else {
      await profileService.updateMyProfile({ avatarUrl: null });
    }
  },

  /**
   * Complete avatar upload process with validation, compression, and profile update
   */
  processAvatarUpload: async (
    file: File, 
    authUserId: string, 
    companyId: string, 
    targetAuthUserId?: string,
    options: AvatarUploadOptions = DEFAULT_AVATAR_OPTIONS
  ): Promise<AvatarUploadResult> => {
    try {
      // Validate file
      profileService.validateImageFile(file, options);

      // Compress image
      const compressedFile = await profileService.compressImage(file, options.quality);

      // Upload to storage (auth user id of the profile owner, for storage RLS)
      const result = await profileService.uploadAvatar(compressedFile, authUserId, companyId);

      // Update the profile row with the new avatar URL
      if (targetAuthUserId) {
        await profileService.updateUserProfile(targetAuthUserId, { avatarUrl: result.url });
      } else {
        await profileService.updateMyProfile({ avatarUrl: result.url });
      }

      return result;
    } catch (error) {
      handleProfileError(error, 'process avatar upload');
      throw error;
    }
  }
};
